import { apiFetch } from '@/lib/api/http';

export type SurveyKind =
  | 'pre'
  | 'post'
  | 'assignment'
  | 'post_assignment'
  | 'day_survey'
  | 'schedule'
  | 'attendance_check'
  | 'director';
/** イベントに割り当てる課題・アンケート（日程調整・出欠確認は別） */
export type EventSurveyKind = Extract<
  SurveyKind,
  'pre' | 'post' | 'assignment' | 'post_assignment' | 'day_survey' | 'director'
>;
export type SurveyAnonymity = 'full' | 'office_only' | 'named';
export type SurveyResultScope = 'office' | 'director_office' | 'respondents' | 'event_participants';
export type SurveyResponseStatus = 'draft' | 'submitted' | 'resubmitted';
export type QuestionType =
  | 'short_text'
  | 'long_text'
  | 'single_choice'
  | 'multi_choice'
  | 'dropdown'
  | 'scale'
  | 'date'
  | 'datetime'
  | 'file'
  | 'matrix'
  | 'description';

export const SURVEY_KIND_LABELS: Record<SurveyKind, string> = {
  pre: '事前アンケート',
  post: '事後アンケート',
  assignment: '事前課題',
  post_assignment: '事後課題',
  day_survey: '当日アンケート',
  schedule: '日程調整',
  attendance_check: '出欠確認',
  director: '理事講師向けアンケート',
};

export const ANONYMITY_LABELS: Record<SurveyAnonymity, string> = {
  full: '完全匿名',
  office_only: '事務局のみ本人確認可',
  named: '記名',
};

export const RESULT_SCOPE_LABELS: Record<SurveyResultScope, string> = {
  office: '事務局のみ',
  director_office: '理事・講師+事務局',
  respondents: '回答者へ集計公開',
  event_participants: 'このイベントの参加者・担当者・事務局',
};

export const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  short_text: '短文',
  long_text: '長文',
  single_choice: '単一選択',
  multi_choice: '複数選択',
  dropdown: 'ドロップダウン',
  scale: '評価',
  date: '日付',
  datetime: '日時',
  file: 'ファイル',
  matrix: 'マトリクス',
  description: '説明文',
};

export interface QuestionOption {
  value: string;
  label: string;
}

export interface QuestionCondition {
  question_id: string;
  operator: 'eq' | 'neq' | 'in' | 'not_in';
  value: unknown;
}

export interface QuestionVisibleIf {
  op: 'all' | 'any';
  conditions: QuestionCondition[];
}

export interface Question {
  id: string;
  type: QuestionType;
  label: string;
  required: boolean;
  /** 選択肢型: 選択肢。マトリクス型: 列（行は rows）。 */
  options?: QuestionOption[] | null;
  /** マトリクス型の行。回答値は {行value: 選択された列value} の辞書。 */
  rows?: QuestionOption[] | null;
  visible_if?: QuestionVisibleIf | null;
}

export interface SurveyTemplate {
  id: string;
  code: string;
  name: string;
  kind: SurveyKind;
  anonymity: SurveyAnonymity;
  result_scope: SurveyResultScope;
  event_type_id: string | null;
  event_type_name: string | null;
  is_active: boolean;
  latest_version: number | null;
  published_version: number | null;
  created_at: string;
  updated_at: string;
}

/** この版を割り当てているイベント（D-129） */
export interface SurveyVersionUsage {
  event_id: string;
  title: string;
  starts_at: string;
}

export interface SurveyTemplateVersion {
  id: string;
  template_id: string;
  version: number;
  questions: Question[];
  published_at: string | null;
  /** どのイベントで使われているか（D-129） */
  used_by: SurveyVersionUsage[];
  created_at: string;
  updated_at: string;
}

export interface EventSurvey {
  id: string;
  event_id: string;
  template_version_id: string;
  template_id: string;
  template_name: string;
  /** 事前・事後・課題の区別（タブの出し分けに使う / D-128） */
  template_kind: SurveyKind;
  version: number;
  questions: Question[];
  opens_at: string | null;
  deadline_at: string | null;
  late_allowed: boolean;
  response_count: number;
  submitted_count: number;
  /** 自分（現在のロール）が集計結果を閲覧できるか（result_scope による動的判定） */
  can_view_results: boolean;
}

export interface SurveyAnswer {
  question_id: string;
  value: unknown;
}

export interface SurveyResponse {
  id: string;
  event_survey_id: string;
  member_id: string | null;
  member_name: string | null;
  /** 理事・講師向け回答の回答者（D-349）。受講生向けでは null */
  respondent_user_id: string | null;
  respondent_name: string | null;
  status: SurveyResponseStatus;
  submitted_at: string | null;
  draft_saved_at: string | null;
  is_overdue: boolean;
  answers: SurveyAnswer[];
}

export interface FileAnswerValue {
  key: string;
  filename: string;
  size: number;
  content_type: string;
}

export interface QuestionResultSummary {
  question_id: string;
  type: QuestionType;
  label: string;
  option_counts: Record<string, number> | null;
  matrix_counts: Record<string, Record<string, number>> | null;
  values: unknown[] | null;
  answered_count: number;
  skipped_count: number;
}

export interface SurveyResults {
  event_survey_id: string;
  submitted_count: number;
  questions: QuestionResultSummary[];
}

// --- テンプレート -----------------------------------------------------------

export function fetchSurveyTemplates(eventTypeId?: string): Promise<SurveyTemplate[]> {
  const q =
    typeof eventTypeId === 'string' && eventTypeId
      ? `?event_type_id=${encodeURIComponent(eventTypeId)}`
      : '';
  return apiFetch<SurveyTemplate[]>(`/survey-templates${q}`);
}

export function createSurveyTemplate(payload: {
  name: string;
  kind: SurveyKind;
  event_type_id: string;
}): Promise<SurveyTemplate> {
  return apiFetch<SurveyTemplate>('/survey-templates', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function updateSurveyTemplate(
  templateId: string,
  payload: { name?: string },
): Promise<SurveyTemplate> {
  return apiFetch<SurveyTemplate>(`/survey-templates/${templateId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function setSurveyTemplateActive(templateId: string, isActive: boolean): Promise<SurveyTemplate> {
  return apiFetch<SurveyTemplate>(`/survey-templates/${templateId}/active`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ is_active: isActive }),
  });
}

export function fetchSurveyVersions(templateId: string): Promise<SurveyTemplateVersion[]> {
  return apiFetch<SurveyTemplateVersion[]>(`/survey-templates/${templateId}/versions`);
}

export function createSurveyVersion(templateId: string, questions: Question[]): Promise<SurveyTemplateVersion> {
  return apiFetch<SurveyTemplateVersion>(`/survey-templates/${templateId}/versions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ questions }),
  });
}

export function updateSurveyVersion(versionId: string, questions: Question[]): Promise<SurveyTemplateVersion> {
  return apiFetch<SurveyTemplateVersion>(`/survey-template-versions/${versionId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ questions }),
  });
}

export function publishSurveyVersion(versionId: string): Promise<SurveyTemplateVersion> {
  return apiFetch<SurveyTemplateVersion>(`/survey-template-versions/${versionId}/publish`, {
    method: 'POST',
  });
}

// --- イベントへの割当 -----------------------------------------------------

export function fetchEventSurveys(eventId: string): Promise<EventSurvey[]> {
  return apiFetch<EventSurvey[]>(`/events/${eventId}/surveys`);
}

export function createEventSurvey(
  eventId: string,
  payload: { template_version_id: string; opens_at?: string | null; deadline_at?: string | null; late_allowed?: boolean },
): Promise<EventSurvey> {
  return apiFetch<EventSurvey>(`/events/${eventId}/surveys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// --- 回答（本人） ---------------------------------------------------------

export function fetchMyResponse(eventSurveyId: string): Promise<SurveyResponse> {
  return apiFetch<SurveyResponse>(`/event-surveys/${eventSurveyId}/responses/me`);
}

export function saveDraft(eventSurveyId: string, answers: SurveyAnswer[]): Promise<SurveyResponse> {
  return apiFetch<SurveyResponse>(`/event-surveys/${eventSurveyId}/responses/me/draft`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  });
}

export function submitResponse(eventSurveyId: string, answers: SurveyAnswer[]): Promise<SurveyResponse> {
  return apiFetch<SurveyResponse>(`/event-surveys/${eventSurveyId}/responses/me/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ answers }),
  });
}

export function uploadAnswerFile(
  eventSurveyId: string,
  questionId: string,
  file: File,
): Promise<FileAnswerValue> {
  const formData = new FormData();
  formData.append('file', file);
  return apiFetch<FileAnswerValue>(
    `/event-surveys/${eventSurveyId}/responses/me/answers/${questionId}/file`,
    { method: 'POST', body: formData },
  );
}

// --- 個別の生回答・集計結果（事務局・サクセス） -----------------------------

export function fetchSurveyResponses(eventSurveyId: string): Promise<SurveyResponse[]> {
  return apiFetch<SurveyResponse[]>(`/event-surveys/${eventSurveyId}/responses`);
}

export function fetchAnswerFileUrl(
  eventSurveyId: string,
  memberId: string,
  questionId: string,
): Promise<{ url: string }> {
  return apiFetch<{ url: string }>(
    `/event-surveys/${eventSurveyId}/responses/${memberId}/answers/${questionId}/file-url`,
  );
}

export function fetchSurveyResults(eventSurveyId: string): Promise<SurveyResults> {
  return apiFetch<SurveyResults>(`/event-surveys/${eventSurveyId}/results`);
}
